import './error-reporting/init';
import React, { ErrorInfo, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './config/i18n';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/sora/700.css';
import AppContainer from './App.container';
import reportWebVitals from './reportWebVitals';
import { ApolloProvider } from '@apollo/client/react';
import { apolloClient } from 'apollo/client';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './error-fallback.component';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import QrCodeManager from './components/navigation/qr-code-manager/qr-code-manager';
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';
import { logoutOnSessionError } from './utils/logout';
import { DenthubErrorBoundaryProvider } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { reportBoundaryError } from './error-reporting/error-reporting';

ReactGA.initialize('G-00YDQEM324');

const handleError = (err: Error, info: ErrorInfo) => {
    console.error(err);
    reportBoundaryError(err, info);
    logoutOnSessionError(err?.message);
};

// The support screen share window (DD-3715) runs without the app shell — no Apollo, auth guard or
// logout handling — so a crash, reload or logout in the Denthub tab cannot stop the share.
const SupportSharePage = lazy(() => import('./support-share/support-share.page'));
const isSupportShareWindow = window.location.pathname === '/support-share';

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
    isSupportShareWindow ? (
        <React.StrictMode>
            <Suspense fallback={<DelayedLinearProgress />}>
                <SupportSharePage />
            </Suspense>
        </React.StrictMode>
    ) : (
        <React.StrictMode>
            <QrCodeManager />
            <ApolloProvider client={apolloClient}>
                <BrowserRouter>
                    <Suspense fallback={<DelayedLinearProgress />}>
                        <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
                            <HelmetProvider>
                                <DenthubErrorBoundaryProvider>
                                    <AppContainer />
                                </DenthubErrorBoundaryProvider>
                            </HelmetProvider>
                        </ErrorBoundary>
                    </Suspense>
                </BrowserRouter>
            </ApolloProvider>
        </React.StrictMode>
    ),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
